import React, { useState, useMemo } from "react";

// Phase 6A.1.5 — preview-before-commit modal for auto-classified
// keyword uploads. Used by both:
//   - CSV import (importCsv classifies entire file, then opens this)
//   - Inline "Add Keyword" with auto-classify (single-row preview)
//
// Operator can edit any taxonomy field per row before clicking Import.
// Rows whose classifier output triggered `needs_review` (any required
// field below the confidence threshold) get an amber left border so
// they stand out. Operator can also remove rows entirely.
//
// Props:
//   open       - boolean
//   onClose    - cancel handler (closes modal, no insert)
//   onImport   - (rows) => Promise. Called when operator clicks Import.
//                Receives the edited rows array. Insert vs update is
//                the parent's call (decided by which mode they pass in
//                + which commit path their handler runs).
//   industry   - parent's activeIndustry, passed through to each row
//   initialRows - array of classifier-output rows. Shape:
//      { keyword, taxonomy_type, taxonomy_service, taxonomy_location,
//        taxonomy_intent, needs_review, ...confidence fields }
//      For update mode (existing rows), each must also carry `id`.
//   title      - optional override (defaults to "Review N classified keywords")
//   sourceLabel - optional small label (e.g. filename) shown in subheader
//   mode       - "insert" (default) | "update". Pure cosmetic in this
//                component — affects only the button label and the
//                eyebrow chip. The actual commit path is the parent's
//                onImport handler.
const TYPES = ["local", "authority"];
const INTENTS = ["commercial", "informational", "how-to"];

export default function KeywordImportPreview({ open, onClose, onImport, industry, initialRows, title, sourceLabel, mode = "insert" }) {
  // Local editable copy of the classifier rows. Initialized when the
  // modal opens; mutations don't propagate to parent until Import is
  // clicked (Cancel discards everything).
  const [rows, setRows] = useState(() => initialRows || []);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");

  // Re-seed state when the parent re-opens with a new batch
  React.useEffect(() => {
    if (open) {
      setRows(initialRows || []);
      setImportError("");
    }
  }, [open, initialRows]);

  // Per-row validity: must have all 4 fields (location only required
  // when type=local). Computed from current state, not the classifier
  // output, so operator edits are reflected immediately.
  const rowReady = (r) => {
    if (!r) return false;
    if (!r.taxonomy_type) return false;
    if (!r.taxonomy_service) return false;
    if (!r.taxonomy_intent) return false;
    if (r.taxonomy_type === "local" && !r.taxonomy_location) return false;
    return true;
  };
  const readyCount = useMemo(() => rows.filter(rowReady).length, [rows]);
  const reviewCount = useMemo(() => rows.filter((r) => r && !rowReady(r)).length, [rows]);

  const patchRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      // Cross-field cleanup: authority can't have a location.
      if (merged.taxonomy_type === "authority") merged.taxonomy_location = null;
      next[idx] = merged;
      return next;
    });
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    setImportError("");
    if (readyCount === 0) {
      setImportError("Nothing to import — every row is missing a required field.");
      return;
    }
    setImporting(true);
    try {
      // Only send rows that are valid. Skipped rows stay in the modal
      // so the operator can finish them in another pass if desired.
      const ready = rows.filter(rowReady);
      await onImport(ready);
    } catch (e) {
      setImportError(e?.message || String(e));
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>{mode === "update" ? "Auto-classify · Tag existing keywords" : "Auto-classify · Review import"}</div>
            <div style={styles.title}>{title || `Review ${rows.length} auto-classified keyword${rows.length === 1 ? "" : "s"}`}</div>
            {sourceLabel && <div style={styles.source}>{sourceLabel}</div>}
            <div style={styles.subtitle}>
              {readyCount} ready · <span style={{ color: reviewCount > 0 ? "#eab308" : "#666" }}>{reviewCount} need review</span> · industry: <strong style={{ color: "#fff" }}>{industry || "—"}</strong>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} disabled={importing}>✕</button>
        </div>

        <div style={styles.body}>
          {rows.length === 0 && (
            <div style={styles.empty}>No keywords to preview.</div>
          )}
          {rows.length > 0 && (
            <div style={styles.tableWrap}>
              <div style={styles.tableHead}>
                <div style={{ ...styles.cell, flex: 3 }}>Keyword</div>
                <div style={{ ...styles.cell, flex: 1.2 }}>Type</div>
                <div style={{ ...styles.cell, flex: 1.6 }}>Service</div>
                <div style={{ ...styles.cell, flex: 1.6 }}>Location</div>
                <div style={{ ...styles.cell, flex: 1.4 }}>Intent</div>
                <div style={{ ...styles.cell, flex: "0 0 36px", textAlign: "right" }}></div>
              </div>
              {rows.map((r, idx) => {
                const ready = rowReady(r);
                return (
                  <div
                    key={idx}
                    style={{
                      ...styles.tableRow,
                      borderLeft: ready ? "2px solid transparent" : "2px solid rgba(234,179,8,0.5)",
                      background: ready ? "transparent" : "rgba(234,179,8,0.04)",
                    }}
                  >
                    <div style={{ ...styles.cell, flex: 3, color: "#fff" }}>{r.keyword}</div>
                    <div style={{ ...styles.cell, flex: 1.2 }}>
                      <select
                        value={r.taxonomy_type || ""}
                        onChange={(e) => patchRow(idx, { taxonomy_type: e.target.value || null })}
                        style={styles.select}
                      >
                        <option value="">type…</option>
                        {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ ...styles.cell, flex: 1.6 }}>
                      <input
                        type="text"
                        placeholder="ac_repair"
                        value={r.taxonomy_service || ""}
                        onChange={(e) => patchRow(idx, { taxonomy_service: e.target.value || null })}
                        style={styles.textInput}
                      />
                    </div>
                    <div style={{ ...styles.cell, flex: 1.6 }}>
                      <input
                        type="text"
                        placeholder={r.taxonomy_type === "authority" ? "(n/a)" : "mount_holly_nc"}
                        disabled={r.taxonomy_type === "authority"}
                        value={r.taxonomy_location || ""}
                        onChange={(e) => patchRow(idx, { taxonomy_location: e.target.value || null })}
                        style={{ ...styles.textInput, opacity: r.taxonomy_type === "authority" ? 0.4 : 1 }}
                      />
                    </div>
                    <div style={{ ...styles.cell, flex: 1.4 }}>
                      <select
                        value={r.taxonomy_intent || ""}
                        onChange={(e) => patchRow(idx, { taxonomy_intent: e.target.value || null })}
                        style={styles.select}
                      >
                        <option value="">intent…</option>
                        {INTENTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ ...styles.cell, flex: "0 0 36px", textAlign: "right" }}>
                      <button
                        onClick={() => removeRow(idx)}
                        style={styles.removeBtn}
                        title="Skip this keyword"
                        disabled={importing}
                      >✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {importError && (
          <div style={styles.errorBanner}>{importError}</div>
        )}

        <div style={styles.footer}>
          <div style={styles.footerHint}>
            {mode === "update"
              ? "Rows missing a required field (amber border) will be skipped on save."
              : "Rows missing a required field (amber border) will be skipped on import."}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={styles.cancelBtn} disabled={importing}>Cancel</button>
            <button
              onClick={handleImport}
              style={{ ...styles.importBtn, opacity: readyCount === 0 || importing ? 0.5 : 1, cursor: readyCount === 0 || importing ? "not-allowed" : "pointer" }}
              disabled={readyCount === 0 || importing}
            >
              {importing
                ? (mode === "update" ? "Saving…" : "Importing…")
                : (mode === "update"
                    ? `Save ${readyCount} keyword${readyCount === 1 ? "" : "s"}`
                    : `Import ${readyCount} keyword${readyCount === 1 ? "" : "s"}`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.65)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  modal: { background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 6, maxWidth: 1100, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 22px", borderBottom: "1px solid #1a1a1a" },
  eyebrow: { fontSize: 10, color: "#d60000", letterSpacing: "0.16em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 4 },
  title: { fontSize: 18, fontWeight: 600, color: "#fff", fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" },
  source: { fontSize: 11, color: "#888", marginTop: 4, fontFamily: "'Barlow', sans-serif" },
  subtitle: { fontSize: 12, color: "#888", marginTop: 6 },
  closeBtn: { background: "none", border: "1px solid #2a2a2a", color: "#888", fontSize: 16, padding: "4px 12px", borderRadius: 3, cursor: "pointer" },
  body: { padding: "12px 22px", overflowY: "auto", flex: 1 },
  empty: { padding: "40px 0", textAlign: "center", color: "#666", fontStyle: "italic" },
  tableWrap: { display: "grid", gap: 4 },
  tableHead: { display: "flex", padding: "6px 8px", fontSize: 10, color: "#666", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, borderBottom: "1px solid #1a1a1a" },
  tableRow: { display: "flex", alignItems: "center", padding: "6px 8px", borderRadius: 3, transition: "background 0.12s" },
  cell: { padding: "0 6px", fontSize: 12, fontFamily: "'Barlow', sans-serif" },
  select: { background: "#111", border: "1px solid #333", color: "#fff", fontSize: 11, fontFamily: "'Barlow Condensed', sans-serif", padding: "5px 8px", outline: "none", borderRadius: 2, width: "100%", letterSpacing: "0.05em" },
  textInput: { background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12, fontFamily: "'Barlow Condensed', sans-serif", padding: "5px 8px", outline: "none", borderRadius: 2, width: "100%" },
  removeBtn: { background: "none", border: "1px solid #2a2a2a", color: "#666", cursor: "pointer", fontSize: 12, padding: "3px 8px", borderRadius: 3 },
  errorBanner: { padding: "10px 22px", color: "#fca5a5", fontSize: 12, background: "rgba(214,0,0,0.08)", borderTop: "1px solid rgba(214,0,0,0.3)" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: "1px solid #1a1a1a", gap: 16 },
  footerHint: { fontSize: 11, color: "#555", fontStyle: "italic" },
  cancelBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#aaa", padding: "8px 18px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
  importBtn: { background: "#d60000", border: "none", color: "#fff", padding: "8px 22px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
};
